import { supabase } from './supabase';
import type { VariantMixEvidence } from './variantMixAnnotation';

/**
 * A catalogued variant of a product model, enriched with its current
 * headline used price (when one exists) and whether it is the variant the
 * user's item is presently linked to. Consumed by the variant picker (T2).
 *
 * `attributes` carries the raw, unnormalized JSONB keys from
 * `product_variants` — the picker is responsible for folding synonym keys
 * and labelling price-relevance, not this read helper.
 */
export interface SiblingVariant {
  variantId: string;
  attributes: Record<string, string>;
  price: { avg: number; currency: string } | null;
  identity_mixed: boolean | null;
  mixed_axes: string[] | null;
  variant_mixed_evidence: VariantMixEvidence | null;
  isCurrent: boolean;
}

interface ProductVariantRow {
  id: string;
  attributes: Record<string, string> | null;
}

interface HeadlinePriceRow {
  variant_id: string | null;
  avg_price: number | null;
  currency: string | null;
  identity_mixed: boolean | null;
  mixed_axes: string[] | null;
  variant_mixed_evidence: VariantMixEvidence | null;
}

/**
 * List every catalogued variant of `modelId`, each joined to its headline
 * used price in one batched round-trip.
 *
 * Read-only and catalog-bound: it returns only variants that already exist
 * in `product_variants`. A variant with no row in
 * `pricing_display_headline_used` comes back with `price: null` (catalogued
 * but not yet priced) rather than being dropped — the picker shows it as
 * "price unavailable" but still selectable.
 *
 * Mirrors the read pattern used by `MarketValuation`: the
 * display view `pricing_display_headline_used` isn't in the generated
 * Supabase types yet, so that one read is cast. Errors degrade gracefully
 * (empty list / null prices) rather than throwing, consistent with the
 * "market data is optional" handling elsewhere.
 *
 * @param modelId          the `product_models.id` whose variants to list.
 * @param currentVariantId the item's current `variant_id`, used to mark
 *                         `isCurrent`. When null/undefined (e.g. the
 *                         late-backfill window where `variant_id` is still
 *                         NULL) no row is marked current.
 */
export async function listSiblingVariants(
  modelId: string,
  currentVariantId?: string | null,
): Promise<SiblingVariant[]> {
  const { data: variantData, error: variantError } = await supabase
    .from('product_variants')
    .select('id, attributes')
    .eq('model_id', modelId);

  // A failed variant read leaves the caller with nothing to choose between;
  // degrade to an empty list rather than surfacing a throw into the UI.
  if (variantError) return [];

  const variants = (variantData ?? []) as ProductVariantRow[];
  if (variants.length === 0) return [];

  const ids = variants.map((v) => v.id);

  // pricing_display_headline_used isn't in the generated types yet; cast the
  // same way MarketValuation does.
  const { data: priceData } = await (supabase as any)
    .from('pricing_display_headline_used')
    .select('variant_id, avg_price, currency, identity_mixed, mixed_axes, variant_mixed_evidence')
    .in('variant_id', ids);

  const priceByVariant = new Map<
    string,
    {
      price: { avg: number; currency: string } | null;
      identity_mixed: boolean | null;
      mixed_axes: string[] | null;
      variant_mixed_evidence: VariantMixEvidence | null;
    }
  >();
  (priceData as HeadlinePriceRow[] | null)?.forEach((row) => {
    if (row.variant_id) {
      priceByVariant.set(row.variant_id, {
        price:
          row.avg_price != null
            ? {
                avg: row.avg_price,
                currency: row.currency ?? 'USD',
              }
            : null,
        identity_mixed: row.identity_mixed ?? null,
        mixed_axes: row.mixed_axes ?? null,
        variant_mixed_evidence: row.variant_mixed_evidence ?? null,
      });
    }
  });

  return variants.map((v) => {
    const headline = priceByVariant.get(v.id);
    return {
      variantId: v.id,
      attributes: v.attributes ?? {},
      price: headline?.price ?? null,
      identity_mixed: headline?.identity_mixed ?? null,
      mixed_axes: headline?.mixed_axes ?? null,
      variant_mixed_evidence: headline?.variant_mixed_evidence ?? null,
      isCurrent: currentVariantId != null && v.id === currentVariantId,
    };
  });
}
